/**
 * Localized URLs for the marketing landing pages.
 *
 * English keeps the original path (/for-salons); every other language gets a
 * locale prefix and a slug in its own language (/de/fuer-friseursalons), so each
 * language has its own indexable URL with hreflang alternates. The URL decides the
 * language of the page (see LocalizedLanding.tsx).
 *
 * A language is left out where the page must not exist in it: pt-BR visitors of the
 * therapists pages are redirected to the Brazil landing instead.
 */
export const LANDING_LANGS = ['en', 'de', 'pt-BR', 'pt-PT', 'pl'] as const;
export type LandingLang = (typeof LANDING_LANGS)[number];

export type LandingId =
  | 'salons'
  | 'barbers'
  | 'beauty'
  | 'spa'
  | 'coaches'
  | 'golf-teachers'
  | 'doctors'
  | 'clinics'
  | 'trainers'
  | 'small-gyms'
  | 'therapists';

export const LOCALIZED_LANDINGS: Record<LandingId, Partial<Record<LandingLang, string>> & { en: string }> = {
  salons: {
    en: '/for-salons',
    de: '/de/fuer-friseursalons',
    'pt-BR': '/pt-br/para-saloes-de-beleza',
    'pt-PT': '/pt-pt/para-cabeleireiros',
    pl: '/pl/dla-salonow-fryzjerskich',
  },
  barbers: {
    en: '/for-barbers',
    de: '/de/fuer-barbershops',
    'pt-BR': '/pt-br/para-barbearias',
    'pt-PT': '/pt-pt/para-barbearias',
    pl: '/pl/dla-barberow',
  },
  beauty: {
    en: '/for-beauty',
    de: '/de/fuer-kosmetikstudios',
    'pt-BR': '/pt-br/para-profissionais-de-beleza',
    'pt-PT': '/pt-pt/para-profissionais-de-beleza',
    pl: '/pl/dla-salonow-kosmetycznych',
  },
  spa: {
    en: '/for-spa',
    de: '/de/fuer-spa-und-wellness',
    'pt-BR': '/pt-br/para-spas',
    'pt-PT': '/pt-pt/para-spas',
    pl: '/pl/dla-spa-i-wellness',
  },
  coaches: {
    en: '/for-coaches',
    de: '/de/fuer-coaches',
    'pt-BR': '/pt-br/para-coaches',
    'pt-PT': '/pt-pt/para-coaches',
    pl: '/pl/dla-coachow',
  },
  'golf-teachers': {
    en: '/for-golf-teachers',
    de: '/de/fuer-golflehrer',
    'pt-BR': '/pt-br/para-professores-de-golfe',
    'pt-PT': '/pt-pt/para-professores-de-golfe',
    pl: '/pl/dla-instruktorow-golfa',
  },
  doctors: {
    en: '/for-doctors',
    de: '/de/fuer-arztpraxen',
    'pt-BR': '/pt-br/para-medicos',
    'pt-PT': '/pt-pt/para-medicos',
    pl: '/pl/dla-lekarzy',
  },
  clinics: {
    en: '/for-clinics',
    de: '/de/fuer-kliniken',
    'pt-BR': '/pt-br/para-clinicas',
    'pt-PT': '/pt-pt/para-clinicas',
    pl: '/pl/dla-klinik',
  },
  trainers: {
    en: '/for-trainers',
    de: '/de/fuer-personal-trainer',
    'pt-BR': '/pt-br/para-personal-trainers',
    'pt-PT': '/pt-pt/para-personal-trainers',
    pl: '/pl/dla-trenerow-personalnych',
  },
  'small-gyms': {
    en: '/for-small-gyms',
    de: '/de/fuer-kleine-fitnessstudios',
    'pt-BR': '/pt-br/para-academias',
    'pt-PT': '/pt-pt/para-ginasios',
    pl: '/pl/dla-malych-silowni',
  },
  therapists: {
    en: '/for-therapists',
    de: '/de/fuer-psychotherapeuten',
    'pt-PT': '/pt-pt/para-psicoterapeutas',
    pl: '/pl/dla-psychoterapeutow',
  },
};

export const SITE_URL = 'https://slotyapp.com';

/** Maps any i18n language code to one of the landing languages (de-AT → de, pt → pt-PT). */
export function toLandingLang(language: string | undefined): LandingLang {
  const lng = language ?? 'en';
  if ((LANDING_LANGS as readonly string[]).includes(lng)) return lng as LandingLang;
  const base = lng.split('-')[0];
  if (base === 'pt') return 'pt-PT';
  return (LANDING_LANGS as readonly string[]).includes(base) ? (base as LandingLang) : 'en';
}

/** Path of a landing page in a language, falling back to English where it has no version. */
export function landingPath(id: LandingId, language: string | undefined): string {
  const paths = LOCALIZED_LANDINGS[id];
  return paths[toLandingLang(language)] ?? paths.en;
}

/** The landing page and language a path belongs to, if it is one of the localized landings. */
export function findLandingByPath(path: string): { id: LandingId; lang: LandingLang } | null {
  const clean = path.replace(/\/+$/, '') || '/';
  for (const id of Object.keys(LOCALIZED_LANDINGS) as LandingId[]) {
    for (const [lang, p] of Object.entries(LOCALIZED_LANDINGS[id])) {
      if (p === clean) return { id, lang: lang as LandingLang };
    }
  }
  return null;
}

/** hreflang alternates for a landing page: one per language it exists in, plus x-default (English). */
export function landingAlternates(id: LandingId): { hrefLang: string; href: string }[] {
  const paths = LOCALIZED_LANDINGS[id];
  return [
    ...Object.entries(paths).map(([lang, p]) => ({ hrefLang: lang, href: `${SITE_URL}${p}` })),
    { hrefLang: 'x-default', href: `${SITE_URL}${paths.en}` },
  ];
}

/** Rewrites an English landing link (/for-salons) to the given language's URL; other hrefs pass through. */
export function localizeHref(href: string, language: string | undefined): string {
  const [path, rest = ''] = href.split(/(?=[?#])/);
  const landing = findLandingByPath(path);
  return landing ? `${landingPath(landing.id, language)}${rest}` : href;
}
